import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { content } from '../data/content';
import { Phone, Wrench, CreditCard, Handshake, DollarSign } from 'lucide-react';

const ServicesContainer = styled.section`
  background: #000000;
  padding: 60px 0 0 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #b7ab9f, transparent);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(138, 138, 126, 0.3), transparent);
  }
`;

const ServicesContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
  background-color: transparent;
`;

const ServicesHeader = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
  position: relative;
`;

const ServicesTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #b7ab9f;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ServicesSubtitle = styled.h3`
  font-size: 1.4rem;
  color: #ffffff;
  margin-bottom: 3rem;
  font-weight: 500;
`;

const ServicesDescription = styled.p`
  font-size: 1.1rem;
  color: #cccccc;
  line-height: 1.7;
  margin-bottom: 3rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  white-space: pre-line;
`;

const ProcessIntro = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  color: #b7ab9f;
  margin: 1.5rem auto 2rem;
  max-width: 750px;
  font-weight: 600;
  text-align: center;
`;

const ServicesList = styled.div`
  margin: 2.5rem 0 2rem;
`;

const ServiceItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.8rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(138, 138, 126, 0.15);

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    padding-bottom: 1.2rem;
  }
`;

const ServiceIcon = styled.div`
  margin-right: 2rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
  
  svg {
    width: 28px;
    height: 28px;
    color: #b7ab9f;
  }

  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 0.8rem;
  }
`;

const ServiceContent = styled.div`
  flex: 1;
`;

const ServiceTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.6rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
`;

const ServiceDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: #cccccc;
`;

const ServicesConclusion = styled.div`
  color: #ffffff;
  padding: 2.5rem 2rem;
  text-align: center;
  position: relative;
  margin-top: 1.5rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #b7ab9f;
  }
`;

const ConclusionText = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: #ffffff;
  font-weight: 500;
  max-width: 650px;
  margin: 0 auto;
`;

const ServicesImageSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 400px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #b7ab9f;
    z-index: 3;
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const ServicesImageText = styled.div`
  background: #000000;
  padding: 40px 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 968px) {
    padding: 30px 20px;
  }
`;

const ImageSectionTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #ffffff;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const ImageSectionText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #cccccc;
`;

const ServicesImageContainer = styled.div`
  position: relative;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.7) 15%, rgba(0, 0, 0, 0.3) 40%, transparent 70%), url('/images/Signing.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 400px;

  @media (max-width: 968px) {
    min-height: 300px;
  }
`;

const PartnersSection = styled.section`
  background: #ffffff;
  padding: 0 0 20px 0;
  text-align: center;
`;

const PartnersContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PartnersBanner = styled.div`
  background: #b7ab9f;
  padding: 20px 0;
  margin-bottom: 0;
`;

const PartnersGradient = styled.div`
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  margin-top: 0;
`;

const PartnersTitle = styled.h2`
  font-size: 3.8rem;
  font-weight: 400;
  margin: 0;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PartnersGrid = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 9.375rem;

  @media (max-width: 968px) {
    gap: 4rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const PartnerLogo = styled.img`
  max-height: 250px;
  max-width: 500px;
  width: auto;
  object-fit: contain;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  &.avantage-plus {
    height: 400px;
    width: 400px;
    max-height: none;
    max-width: none;
  }

  &.first-canadian {
    max-height: 153px;
    max-width: none;
    width: auto;
  }

  @media (max-width: 968px) {
    max-height: 180px;
    max-width: 350px;

    &.avantage-plus {
      height: 280px;
      width: 280px;
    }

    &.first-canadian {
      max-height: 120px;
    }
  }

  @media (max-width: 768px) {
    max-height: 120px;
    max-width: 280px;

    &.avantage-plus {
      height: 180px;
      width: 180px;
    }

    &.first-canadian {
      max-height: 80px;
    }
  }

  @media (max-width: 480px) {
    max-height: 100px;
    max-width: 220px;

    &.avantage-plus {
      height: 140px;
      width: 140px;
    }

    &.first-canadian {
      max-height: 65px;
    }
  }
`;

const CrossedDollarIcon = () => (
  <img
    src="/images/CrossedDollar.png"
    alt="No fees for dealership"
    style={{ width: '44px', height: '44px', marginLeft: '-8px' }}
  />
);

const Services = () => {
  const { language } = useLanguage();
  const t = content[language];

  const serviceIcons = [Phone, Wrench, CreditCard, Handshake, CrossedDollarIcon];

  return (
    <>
      <ServicesContainer id="services">
        <ServicesContent>
          <ServicesHeader>
            <ServicesTitle className="content-entry-animation">{t.services.title}</ServicesTitle>
            <ServicesSubtitle className="content-entry-animation">{t.services.subtitle}</ServicesSubtitle>
            <ServicesDescription className="content-entry-animation">{t.services.description}</ServicesDescription>
            <ProcessIntro className="content-entry-animation">{t.services.process_intro}</ProcessIntro>
          </ServicesHeader>

          <ServicesList>
            {t.services.items.map((service, index) => {
              const IconComponent = serviceIcons[index];
              return (
                <ServiceItem key={index} className="content-entry-animation">
                  <ServiceIcon>
                    <IconComponent />
                  </ServiceIcon>
                  <ServiceContent>
                    <ServiceTitle>{service.title}</ServiceTitle>
                    <ServiceDescription>{service.description}</ServiceDescription>
                  </ServiceContent>
                </ServiceItem>
              );
            })}
          </ServicesList>

          <ServicesConclusion>
            <ConclusionText className="content-entry-animation">{t.services.conclusion}</ConclusionText>
          </ServicesConclusion>
        </ServicesContent>

        <ServicesImageSection>
          <ServicesImageText>
            <ImageSectionTitle className="content-entry-animation">
              {language === 'fr'
                ? "Processus professionnel"
                : "Professional process"
              }
            </ImageSectionTitle>
            <ImageSectionText className="content-entry-animation">
              {language === 'fr'
                ? "Chez Groupe Garant, nous croyons qu'un service structuré et rigoureux est la clé d'une performance durable. Chaque étape de notre service est soigneusement documentée pour assurer la transparence et la qualité de nos interventions, de la prise de contact initiale jusqu'au suivi post-service. Ce souci du détail nous permet d'optimiser les résultats tout en inspirant la confiance de nos partenaires. Pour garantir un niveau d'excellence constant, nous collaborons exclusivement avec des partenaires méticuleusement sélectionnés et de confiance."
                : "At Groupe Garant, we believe that a structured and rigorous service is the key to sustainable performance. Every step of our service is carefully documented to ensure transparency and quality in our interventions, from initial contact to post-service follow-up. This attention to detail allows us to optimize results while inspiring confidence in our partners. To guarantee a constant level of excellence, we work exclusively with meticulously selected and trusted partners."
              }
            </ImageSectionText>
          </ServicesImageText>

          <ServicesImageContainer />
        </ServicesImageSection>
      </ServicesContainer>

      <PartnersSection>
        <PartnersBanner>
          <PartnersTitle className="content-entry-animation">
            {language === 'fr' ? 'Nos Partenaires' : 'Our Partners'}
          </PartnersTitle>
        </PartnersBanner>
        <PartnersGradient>
          <PartnersContainer>
            <PartnersGrid>
              <a
                href="https://www.garantieavantageplus.ca/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <PartnerLogo
                  className="content-entry-animation avantage-plus"
                  src="/images/AdvantagePlusColor.svg"
                  alt="Advantage Plus"
                />
              </a>
              <a
                href="https://home.firstcanadian.ca/fr/vehicle-protection/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <PartnerLogo
                  className="content-entry-animation first-canadian"
                  src="/images/GroupeFC.jpg"
                  alt="First Canadian"
                />
              </a>
            </PartnersGrid>
          </PartnersContainer>
        </PartnersGradient>
      </PartnersSection>
    </>
  );
};

export default Services; 
